import type {AppPayload} from '@github-ui/issues-shared/app-payload'
/* eslint-disable-next-line @github-ui/github-monorepo/no-use-app-payload -- Cannot use UI Service because we're within Relay environment */
import {useAppPayload} from '@github-ui/react-core/use-app-payload'
import {useFeatureFlag} from '@github-ui/react-core/use-feature-flag'
import {useParams} from '@github-ui/react-router'
import type {SidebarPinnedView} from '@github-ui/repos-issues-sidebar-ui/PinnedViewsNav'
import {RepoSavedViewsNav} from '@github-ui/repos-issues-sidebar-ui/RepoSavedViewsNav'
import {ReposIssuesSidebar as SharedRepoIssuesSidebar} from '@github-ui/repos-issues-sidebar-ui/ReposIssuesSidebar'
import {usePinnedSavedViews} from '@github-ui/saved-views/hooks/use-pinned-saved-views'
import {useMemo} from 'react'

import {usePreloadSidebarPages} from '../../hooks/use-preload-sidebar-pages'
import type {SavedViewsResponse} from '../views/types'

export function RepoIssuesSidebar({collapsible}: {collapsible?: boolean} = {}) {
  usePreloadSidebarPages()
  const {owner, repo} = useParams<{owner: string; repo: string}>()
  const {scoped_repository, saved_views} = useAppPayload<AppPayload>()
  const hasProjectsEnabled = scoped_repository?.has_projects_enabled ?? false
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags -- issue_pinned_views uses actor-aware server-side evaluation via PinnedViewsFeature; isFeatureEnabled only knows current_user. */
  const pinnedViewsEnabled = useFeatureFlag('issue_pinned_views')
  /* eslint-disable-next-line @github-ui/github-monorepo/no-use-feature-flags -- issue_pinned_views_team_vs_personal uses actor-aware server-side evaluation via PinnedViewsFeature; isFeatureEnabled only knows current_user. */
  const pinnedViewsTeamVsPersonalEnabled = useFeatureFlag('issue_pinned_views_team_vs_personal')
  const pinnedViews = usePinnedViews(owner ?? '', repo ?? '', pinnedViewsEnabled, saved_views ?? undefined)
  return (
    <SharedRepoIssuesSidebar
      owner={owner ?? ''}
      repo={repo ?? ''}
      collapsible={collapsible}
      hasProjectsEnabled={hasProjectsEnabled}
      pinnedViews={pinnedViews}
      pinnedViewsTeamVsPersonalEnabled={pinnedViewsTeamVsPersonalEnabled}
      renderSavedViewsNav={
        pinnedViewsEnabled ? undefined : ctx => <RepoSavedViewsNav owner={owner ?? ''} repo={repo ?? ''} {...ctx} />
      }
    />
  )
}

function usePinnedViews(
  owner: string,
  repo: string,
  enabled: boolean,
  initialData?: SavedViewsResponse,
): SidebarPinnedView[] {
  const {pinnedViews} = usePinnedSavedViews({owner, repo, enabled, initialData})
  return useMemo(
    () =>
      pinnedViews.map(view => ({
        id: view.id,
        name: view.name,
        icon: view.icon,
        color: view.color,
        visibility: view.visibility,
      })),
    [pinnedViews],
  )
}
